import { createSelector } from 'reselect';
import { RootState } from '../../../types';
import { EntityTypes } from '../../../constants/entities';
import { IEntityReducer } from '../../reducers/entities';
import { IModule, IModuleAssign, IModuleDetailDAO, IModuleVersion } from 'types/domain/module';
import { getEntities } from '../utils/getEntities';

import { ITagJoin } from 'types/domain/tag';
import { parseVariableDAO } from '../admin/variable';
import { selectSessionMode } from '../admin/sessionMode';
import { Mode } from 'core/slices/session';
import { IPreviewState } from 'core/slices/preview';
import { createCachedSelector } from 're-reselect';
import { ICourseNode } from 'types/domain/course';

const entity = (state: RootState) => state.entity.present;
const id = (state: RootState, id?: number) => (id ? id : state.state[EntityTypes.MODULE]);
const mode = selectSessionMode;
const preview = (state: RootState) => state.preview;

export const selectModuleCached = createCachedSelector(
  entity,
  id,
  parseModuleDAO,
)((state, id) => id || state.state[EntityTypes.MODULE] || 'unknown');
export const selectModule = createSelector([entity, id], parseModule);
export const selectModuleDAO = createSelector([entity, id], parseModuleDAO);
export const selectModuleVariable = createSelector([entity, id], parseModuleVariable);
export const selectModuleActionsLength = createSelector([entity, id], parseModuleActionsLength);
export const selectModuleAssign = createSelector([entity, id, mode, preview], parseModuleAssign);

export function parseModule(entity: IEntityReducer, id: number) {
  const module: IModule = entity[EntityTypes.MODULE][id];
  const text = module && module.textId ? entity[EntityTypes.TEXT][module.textId] : null;
  return module
    ? ({
        ...module,
        name: text ? text.shortText || module.name : module.name,
        image: module.imageId ? entity[EntityTypes.IMAGE][module.imageId] : null,
        text,
        variable: parseVariableDAO(entity, module.variableId),
        versions: getEntities(entity, EntityTypes.MODULE_VERSION, 'moduleId', id) as IModuleVersion[],
        tags: getEntities(entity, EntityTypes.TAG_JOIN, 'moduleId', id) as ITagJoin[],
      } as IModule)
    : null;
}

export function parseModuleVariable(entity: IEntityReducer, id: number) {
  const module: IModule = entity[EntityTypes.MODULE][id];

  return module ? parseVariableDAO(entity, module.variableId) : null;
}

export function parseModuleActionsLength(entity: IEntityReducer, id: number) {
  const module: IModule = entity[EntityTypes.MODULE][id];

  if (!module) return 0;

  return module.actions ? module.actions.length : getEntities(entity, EntityTypes.ACTION, 'moduleId', id).length;
}

export function parseModuleDAO(entity: IEntityReducer, id: number) {
  const module: IModule = entity[EntityTypes.MODULE][id];
  const actions = getEntities(entity, EntityTypes.ACTION, 'moduleId', id);
  const outs = getEntities(entity, EntityTypes.MODULE_OUT, 'moduleId', id);
  const text = module && module.textId ? entity[EntityTypes.TEXT][module.textId] : null;
  const courseNodes: ICourseNode[] = getEntities(entity, EntityTypes.COURSE_NODE, 'moduleId', id);

  return module
    ? ({
        ...module,
        name: text ? text.shortText : module.name,
        image: module.imageId ? entity[EntityTypes.IMAGE][module.imageId] : null,
        text,
        variable: parseVariableDAO(entity, module.variableId),
        outs,
        actions,
        courseNodes,
        courseName: courseNodes.length ? entity[EntityTypes.COURSE][courseNodes[0].courseId]?.name ?? null : null,
        //versions: getEntities(entity, EntityTypes.MODULE_VERSION, 'moduleId', id) as IModuleVersion[],
        //tags: getEntities(entity, EntityTypes.TAG_JOIN, 'moduleId', id) as ITagJoin[],
      } as IModuleDetailDAO)
    : null;
}

export function parseModuleAssign(entity: IEntityReducer, id: number, mode: Mode, preview: IPreviewState) {
  const module: IModuleAssign = entity[EntityTypes.MODULE][id] as IModuleAssign;

  return module
    ? ({
        ...module,
        sessionAttemptId:
          mode === Mode.PREVIEW
            ? preview.modules[id]
              ? preview.modules[id].sessionAttemptId
              : null
            : module.sessionAttemptId,
        sessionAttempt: mode === Mode.LEARNING ? entity[EntityTypes.SESSION_ATTEMPT][module.sessionAttemptId] : null,
      } as IModuleAssign)
    : null;
}

export const selectModuleTextId = createSelector(
  entity,
  id,
  (entity, id) => entity[EntityTypes.TEXT][entity[EntityTypes.MODULE][id]?.textId]?.id,
);

export const selectModuleType = createSelector(entity, id, (entity, id) => entity[EntityTypes.MODULE][id]?.type);
